import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
import { environment } from 'src/environments/environment';

// Initialize Firebase Analytics once at the root
FirebaseAnalytics.initializeFirebase(environment.firebase);

@Injectable({
  providedIn: 'root'
})
export class EventsService {

  constructor() {
    // Enable debug mode if environment variable is set
    if (environment.enableDebug) {
      localStorage.setItem('firebase_analytics_debug_mode', 'true');
    } else {
      localStorage.removeItem('firebase_analytics_debug_mode');
    }
  }

  /**
   * Example usage:
   * this.eventsService.setUserId('user_123');
   */
  setUserId(uid: string) {
    console.log("setUserId", uid);
    FirebaseAnalytics.setUserId({
      userId: uid,
    }).then((snap) => {
      console.log("snap", snap);
      console.log("setUserId success");
    });
  }

  /**
   * Example usage:
   * this.eventsService.setUserProperty('favorite_food', 'pizza');
   */
  setUserProperty(name: string, value: string) {
    FirebaseAnalytics.setUserProperty({
      name: name,
      value: value,
    });
  }

  /**
   * A general-purpose logEvent method, if you want to use custom event names
   * and parameters without creating a separate method for each.
   */
  logCustomEvent(eventName: string, params: any = {}) {
    FirebaseAnalytics.logEvent({
      name: eventName,
      params,
    });
  }

  /**
   * Screen names, session timeouts, etc.
   */
  setScreenName(screenName: string) {
    FirebaseAnalytics.setScreenName({ screenName });
  }

  setSessionTimeoutDuration(duration: number) {
    FirebaseAnalytics.setSessionTimeoutDuration({ duration });
  }

  /* ------------------------------------------------------------------------
   * BELOW ARE EXAMPLE METHODS FOR SPECIFIC EVENTS YOU MENTIONED
   * ------------------------------------------------------------------------
   */

  /* ----------------------
   * 1. Virality Events
   * ---------------------- */
  logInviteInitiated(inviteMethod: string, referralCode?: string) {
    FirebaseAnalytics.logEvent({
      name: "invite_initiated",
      params: {
        invite_method: inviteMethod,
        referral_code: referralCode,
      },
    });
  }

  logInviteAccepted(referrerId: string, referralCode?: string) {
    FirebaseAnalytics.logEvent({
      name: "invite_accepted",
      params: {
        referrer_id: referrerId,
        referral_code: referralCode,
      },
    });
  }

  logWaitingListSignup(canSkipLine: boolean, referralCode?: string) {
    FirebaseAnalytics.logEvent({
      name: "waiting_list_signup",
      params: {
        can_skip_line: canSkipLine,
        referral_code: referralCode,
      },
    });
  }

  logSkipLineAttempt(inviteCount: number) {
    FirebaseAnalytics.logEvent({
      name: "skip_line_attempt",
      params: {
        invite_count: inviteCount,
      },
    });
  }

  logContentShared(sharePlatform: string, contentType: string, agentId?: string) {
    FirebaseAnalytics.logEvent({
      name: "content_shared",
      params: {
        share_platform: sharePlatform,
        content_type: contentType,
        agent_id: agentId,
      },
    });
  }

  logCollaborationInviteSent(teamSize: number, collaborationMethod: string) {
    FirebaseAnalytics.logEvent({
      name: "collaboration_invite_sent",
      params: {
        team_size: teamSize,
        collaboration_method: collaborationMethod,
      },
    });
  }

  logExclusiveInviteRequested(waitlistPosition?: number) {
    FirebaseAnalytics.logEvent({
      name: "exclusive_invite_requested",
      params: {
        waitlist_position: waitlistPosition,
      },
    });
  }

  logOfferUnlocked(offerType: string, referralCode?: string) {
    FirebaseAnalytics.logEvent({
      name: "offer_unlocked",
      params: {
        offer_type: offerType,
        referral_code: referralCode,
      },
    });
  }

  /* ----------------------
   * 2. Social Media & Content
   * ---------------------- */
  logSocialLinkClicked(sourcePlatform: string, campaignId?: string) {
    FirebaseAnalytics.logEvent({
      name: "social_link_clicked",
      params: {
        source_platform: sourcePlatform,
        campaign_id: campaignId,
      },
    });
  }

  logVideoViewed(videoTitle: string, videoDuration?: number, viewDuration?: number) {
    FirebaseAnalytics.logEvent({
      name: "video_viewed",
      params: {
        video_title: videoTitle,
        video_duration: videoDuration,
        view_duration: viewDuration,
      },
    });
  }

  logChallengeParticipation(challengeName: string) {
    FirebaseAnalytics.logEvent({
      name: "challenge_participation",
      params: {
        challenge_name: challengeName,
      },
    });
  }

  logCaseStudyViewed(caseStudyId: string, industry?: string) {
    FirebaseAnalytics.logEvent({
      name: "case_study_viewed",
      params: {
        case_study_id: caseStudyId,
        industry: industry,
      },
    });
  }

  logInfluencerLinkClicked(influencerId: string, campaignId?: string) {
    FirebaseAnalytics.logEvent({
      name: "influencer_link_clicked",
      params: {
        influencer_id: influencerId,
        campaign_id: campaignId,
      },
    });
  }

  /* ----------------------
   * 3. Community Building
   * ---------------------- */
  logWebinarRegistration(webinarTopic: string, date: string) {
    FirebaseAnalytics.logEvent({
      name: "webinar_registration",
      params: {
        webinar_topic: webinarTopic,
        date: date,
      },
    });
  }

  logWebinarAttendance(webinarTopic: string, attendanceDuration: number) {
    FirebaseAnalytics.logEvent({
      name: "webinar_attendance",
      params: {
        webinar_topic: webinarTopic,
        attendance_duration: attendanceDuration,
      },
    });
  }

  logCommunityJoined(platform: string) {
    FirebaseAnalytics.logEvent({
      name: "community_joined",
      params: {
        platform: platform, // e.g., Slack or Discord
      },
    });
  }

  logCommunityPost(postType: string) {
    FirebaseAnalytics.logEvent({
      name: "community_post",
      params: {
        post_type: postType, // e.g., question, tip, announcement
      },
    });
  }

  logLocalEventSignup(eventLocation: string, eventName: string) {
    FirebaseAnalytics.logEvent({
      name: "local_event_signup",
      params: {
        event_location: eventLocation,
        event_name: eventName,
      },
    });
  }

  /* ----------------------
   * 4. Product-Led Growth
   * ---------------------- */
  logFreeTierSignup(signupMethod: string, source?: string) {
    FirebaseAnalytics.logEvent({
      name: "free_tier_signup",
      params: {
        signup_method: signupMethod,
        source: source,
      },
    });
  }

  logUpgradeAttempt(planType: string, paymentMethod?: string) {
    FirebaseAnalytics.logEvent({
      name: "upgrade_attempt",
      params: {
        plan_type: planType,
        payment_method: paymentMethod,
      },
    });
  }

  logSubscriptionStart(planType: string, paymentMethod?: string) {
    FirebaseAnalytics.logEvent({
      name: "subscription_start",
      params: {
        plan_type: planType,
        payment_method: paymentMethod,
      },
    });
  }

  logSubscriptionRenewal(planType: string, renewalSuccess: boolean) {
    FirebaseAnalytics.logEvent({
      name: "subscription_renewal",
      params: {
        plan_type: planType,
        renewal_success: renewalSuccess,
      },
    });
  }

  logBadgeEarned(badgeName: string) {
    FirebaseAnalytics.logEvent({
      name: "badge_earned",
      params: {
        badge_name: badgeName,
      },
    });
  }

  logLeaderboardViewed(position?: number, points?: number) {
    FirebaseAnalytics.logEvent({
      name: "leaderboard_viewed",
      params: {
        position: position,
        points: points,
      },
    });
  }

  logOnboardingStepCompleted(stepName: string, stepSequence: number) {
    FirebaseAnalytics.logEvent({
      name: "onboarding_step_completed",
      params: {
        step_name: stepName,
        step_sequence: stepSequence,
      },
    });
  }

  logFirstAgentCreated(agentType: string) {
    FirebaseAnalytics.logEvent({
      name: "first_agent_created",
      params: {
        agent_type: agentType,
      },
    });
  }

  logAgentRunExecuted(agentType: string, outcome: string) {
    FirebaseAnalytics.logEvent({
      name: "agent_run_executed",
      params: {
        agent_type: agentType,
        outcome: outcome, // e.g., success or error
      },
    });
  }

  /* ----------------------
   * 5. Localization
   * ---------------------- */
  logLanguageSelected(language: string) {
    FirebaseAnalytics.logEvent({
      name: "language_selected",
      params: {
        language: language,
      },
    });
  }

  logRegionSpecificOfferClicked(region: string, offerType: string) {
    FirebaseAnalytics.logEvent({
      name: "region_specific_offer_clicked",
      params: {
        region: region,
        offer_type: offerType,
      },
    });
  }

  /* ----------------------
   * 6. Paid Advertising & Retargeting
   * ---------------------- */
  logAdClick(adNetwork: string, campaignId: string, adGroup?: string) {
    FirebaseAnalytics.logEvent({
      name: "ad_click",
      params: {
        ad_network: adNetwork,
        campaign_id: campaignId,
        ad_group: adGroup,
      },
    });
  }

  logLandingPageView(campaignId: string, landingPageUrl: string) {
    FirebaseAnalytics.logEvent({
      name: "landing_page_view",
      params: {
        campaign_id: campaignId,
        landing_page_url: landingPageUrl,
      },
    });
  }

  logConversion(conversionType: string, campaignId?: string) {
    FirebaseAnalytics.logEvent({
      name: "conversion",
      params: {
        conversion_type: conversionType,
        campaign_id: campaignId,
      },
    });
  }

  logRemarketingImpression(adPlatform: string, adContent?: string) {
    FirebaseAnalytics.logEvent({
      name: "remarketing_impression",
      params: {
        ad_platform: adPlatform,
        ad_content: adContent,
      },
    });
  }

  logRemarketingClick(adPlatform: string, offerType?: string) {
    FirebaseAnalytics.logEvent({
      name: "remarketing_click",
      params: {
        ad_platform: adPlatform,
        offer_type: offerType,
      },
    });
  }

  logAffiliateReferral(affiliateId: string, affiliateCommission?: number) {
    FirebaseAnalytics.logEvent({
      name: "affiliate_referral",
      params: {
        affiliate_id: affiliateId,
        affiliate_commission: affiliateCommission,
      },
    });
  }

  /* ----------------------
   * 7. PR & Media
   * ---------------------- */
  logPressPageView(source: string) {
    FirebaseAnalytics.logEvent({
      name: "press_page_view",
      params: {
        source: source,
      },
    });
  }

  logAwardSubmission(awardName: string) {
    FirebaseAnalytics.logEvent({
      name: "award_submission",
      params: {
        award_name: awardName,
      },
    });
  }

  logTestimonialViewed(brandName: string) {
    FirebaseAnalytics.logEvent({
      name: "testimonial_viewed",
      params: {
        brand_name: brandName,
      },
    });
  }

}
